// src/store/actions/bookActions.ts
import { createAsyncThunk } from '@reduxjs/toolkit';
import { tr } from '../../i18n/i18n';
import i18n from '../../i18n/i18n';
import axios, { AxiosError, AxiosResponse } from 'axios';
import {
  BookLendinRequestBody,
  BookLendOutRequestBody,
  BookLendResponse,
  BookQueryParams,
  BooksApiResponse,
  CreateBooksApiResponse,
  DeleteBookRequestBody,
  DeleteBookResponseBody,
  GenreResponse,
  IBulkUploadResponse,
  ICreateBookRequestBody,
  IUpdateBookRequest,
  UpdateBookApiResponse,
} from '../../interface/libraryManagement';
import {
  allBookData,
  allGenreTypes,
  bookTemplate,
  bulkUpload,
  deleteSelectedBook,
  lendinBook,
  lendOutBook,
  patchBook,
  postBook,
} from '../../services/libraryManagement';
import { IErrorResponse } from '../../interface/main.interface';

export const getAllBooks = createAsyncThunk<
  BooksApiResponse,
  BookQueryParams | undefined,
  { rejectValue: IErrorResponse }
>('book/get-all', async (params, { rejectWithValue }) => {
  try {
    const response = await allBookData(params);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const createBook = createAsyncThunk<
  CreateBooksApiResponse,
  ICreateBookRequestBody,
  { rejectValue: IErrorResponse }
>('book/create', async (payload, { rejectWithValue }) => {
  try {
    const response = await postBook(payload);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const updateBook = createAsyncThunk<
  UpdateBookApiResponse,
  IUpdateBookRequest,
  { rejectValue: IErrorResponse }
>('book/update', async (payload, { rejectWithValue }) => {
  try {
    const response = await patchBook(payload);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const bookLendin = createAsyncThunk<
  BookLendResponse,
  BookLendinRequestBody,
  { rejectValue: IErrorResponse }
>('book/lend-in', async (payload, { rejectWithValue }) => {
  try {
    const response = await lendinBook(payload);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const bookLendOut = createAsyncThunk<
  BookLendResponse,
  BookLendOutRequestBody,
  { rejectValue: IErrorResponse }
>('book/lend-out', async (payload, { rejectWithValue }) => {
  try {
    const response = await lendOutBook(payload);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const deleteBook = createAsyncThunk<
  DeleteBookResponseBody,
  DeleteBookRequestBody,
  { rejectValue: IErrorResponse }
>('book/delete', async (payload, { rejectWithValue }) => {
  try {
    const response = await deleteSelectedBook(payload);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const getAllGenreTypes = createAsyncThunk<
  GenreResponse,
  void,
  { rejectValue: IErrorResponse }
>('book/get-all-genre-types', async (_, { rejectWithValue }) => {
  try {
    const response = await allGenreTypes();
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const bookBulkUpload = createAsyncThunk<
  IBulkUploadResponse,
  FormData,
  { rejectValue: IErrorResponse }
>('book/create-bulk', async (file, { rejectWithValue }) => {
  try {
    const response = await bulkUpload(file);
    return response;
  } catch (err: any) {
    if (axios.isAxiosError(err)) {
      const error = err as AxiosError<IErrorResponse>;
      return rejectWithValue({
        status: error.response?.status || 500,
        message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
      });
    } else {
      return rejectWithValue({ status: err.status || 500, message: err.message });
    }
  }
});

export const getBookTemplate = createAsyncThunk<void, void, { rejectValue: IErrorResponse }>(
  'book/get-book-template',
  async (_, { rejectWithValue }) => {
    try {
      await bookTemplate();
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        const error = err as AxiosError<IErrorResponse>;
        return rejectWithValue({
          status: error.response?.status || 500,
          message: error.response?.data?.message || i18n.t('toast.unknown_error', 'Unknown error'),
        });
      } else {
        return rejectWithValue({ status: err.status || 500, message: err.message });
      }
    }
  },
);
